<template>
    <div>

        <vue-headful title="Training Course Email Alert :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                    <router-link :to="`/training-courses`">
                        <i class="fa fa-graduation-cap"></i> Training Courses
                    </router-link>
                </li>
                <li class="breadcrumb-item font-weight-light d-block d-md-inline-block mt-md-0 mt-2">
                    <router-link :to="`/training-courses/alerts`">
                        Expiring Course Alerts
                    </router-link>
                </li>
                <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">
                    <template v-if="!id">New Alert</template>
                    <template v-else>
                        <template v-if="!loading">Edit {{ alert.alert_name }}</template>
                    </template>
                </li>
            </ol>
        </h4>

        <div v-if="loading">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <div class="row">
                <div class="col-lg-8 col-12">
                    <div class="card mb-3">
                        <div class="card-header">Alert Details</div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div :class="{'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.alert_name ,'text-muted': !errors.alert_name}">
                                    Alert Name: <i class="fa fa-asterisk small text-danger"></i>
                                </div>
                                <div class="col-md-8">
                                    <input type="text" name="alert_name" v-model="alert.alert_name" :class="{'form-control': true, 'is-invalid': errors.alert_name}" placeholder="Enter Alert Name" />

                                    <span v-if="errors.alert_name" class="text-danger">
                                        <span v-for="error in errors.alert_name">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div :class="{'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.days_until_expires ,'text-muted': !errors.days_until_expires}">
                                    Days Until Expiration: <i class="fa fa-asterisk small text-danger"></i>
                                </div>
                                <div class="col-md-8">
                                    <input type="number" name="days_until_expires" v-model="alert.days_until_expires" :class="{'form-control': true, 'is-invalid': errors.days_until_expires}" placeholder="Enter the Number of Days" />
                                    <span class="text-info small mt-1">This is the number of days away from a course expiring that the email will be triggered.</span>

                                    <span v-if="errors.days_until_expires" class="text-danger mt-2">
                                        <span v-for="error in errors.days_until_expires">{{ error }}</span>
                                    </span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div :class="{'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.is_active ,'text-muted': !errors.is_active}">Active:</div>
                                <div class="col-md-8">
                                    <b-form-checkbox v-model="alert.is_active" :value="1" :unchecked-value="0" plain />
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div :class="{'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.description ,'text-muted': !errors.description}">Description:</div>
                                <div class="col-md-8">
                                    <textarea class="form-control" placeholder="Enter Alert Description (Optional)" v-model="alert.description" rows="2">{{ alert.description }}</textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card mb-3">
                        <div class="card-header">Course Filters <small class="text-muted">Leave blank to include ALL training courses</small></div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div :class="{'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-muted': !errors.filter_on_courses}">Add a Course:</div>
                                <div class="col-md-8">
                                    <v-select ref="courseSelect" label="course_name" @input="courseSelected" :options="courses" :placeholder="`Search Training Courses`"></v-select>

                                    <ul class="mt-3 list-unstyled">
                                        <li v-for="(course, i) in alert.filter_on_courses" class="mb-2">
                                            <button class="btn btn-xs btn-default" v-on:click="removeCourse(i)"><i class="fa fa-trash text-danger"></i></button> <strong>{{ course.course_name }}</strong>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header">
                            Recipients
                            <button class="btn btn-sm btn-info" type="button" v-on:click="recipients.push({target_type: null, target_value: null})"><i class="fa fa-plus"></i> Add</button>
                        </div>
                        <div class="card-body form-inline">
                            <strong>Send Email To:</strong>
                            <ol>
                                <li v-for="(recip, i) in recipients" class="mb-2">
                                    <a href="javascript:void" class="text-danger btn-link" v-on:click="deleteRecipient(i);"><i class="fa fa-trash"></i></a>

                                    <b-form-select class="form-control" v-model="recip.target_type" :options="recipientOptions" />

                                    <span>=</span>

                                    <span v-if="recip.target_type == 'Employee'" class="d-lg-inline-block" style="width: 400px;">
                                        <employee-select
                                            :selectedValues="recip.target_label ? {EmployeeId: recip.target_label.EmployeeId, FirstName: recip.target_label.FirstName, LastName: recip.target_label.LastName, EmployeeNumber: recip.target_label.EmployeeNumber} : null"
                                            @vselected="function(employee){ recip.target_value = employee.EmployeeId; }"
                                            :hasError="errors['recipients['+i+']'] ? true : false" />
                                    </span>

                                    <template v-if="recip.target_type == 'Employee Title'">
                                        <!-- <input type="text" v-model="recip.target_value" placeholder="Enter an Employee Title/Position" class="form-control" /> -->
                                        <b-form-select v-model="recip.target_value" :options="titleOptions" :class="{'is-invalid': errors['recipients['+i+']']}" />
                                    </template>

                                    <template v-if="recip.target_type == 'Email'">
                                        <input type="text" v-model="recip.target_value" placeholder="Enter an Email Address" class="form-control" />
                                    </template>

                                    <template v-if="recip.target_type == 'Role'">
                                        <b-form-select class="form-control" v-model="recip.target_value" :options="roleOptions" />
                                    </template>

                                    <span v-if="recip.target_type == 'User'" class="d-lg-inline-block" style="width: 400px;">
                                        <user-select
                                            :selectedValues="recip.target_label ? {UserId: recip.target_label.UserId, FirstName: recip.target_label.FirstName, LastName: recip.target_label.LastName} : null"
                                            @vuselected="function(user){ recip.target_value = user.UserId; }"
                                            :hasError="errors['recipients['+i+']'] ? true : false" />
                                    </span>

                                    <span v-if="errors['recipients['+i+']']" class="text-danger">
                                        <br>
                                        <span v-for="(error, key) in errors['recipients['+i+']']"><template v-if="key > 0">, </template>{{ error }}</span>
                                    </span>
                                </li>
                            </ol>

                            <div v-if="errors.recipients" class="alert alert-danger">
                                <span v-for="(error, key) in errors.recipients"><template v-if="key > 0">, </template>{{ error }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr/>

            <button type="button" class="btn btn-success" v-on:click="saveAlert()" :disabled="saving">
                <template v-if="saving"><i class="fa fa-spinner fa-spin"></i> Saving</template>
                <template v-else>Save Training Course Alert</template>
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                id: this.$route.params.id,
                loading: true,
                saving: false,
                errors: {},
                alert: {},
                recipients: [],
                recipientTypes: [],
                titles: [],
                courses: [],
            }
        },

        computed: {

            recipientOptions(){
                //Build it
                var options = [
                    {value: null, text: '-- Select a Target Type --'}
                ];

                //Loop
                for(var i = 0; i < this.recipientTypes.length; i++)
                {
                    //Push
                    options.push({value: this.recipientTypes[i], text: this.recipientTypes[i]});
                }

                //Send it
                return options;
            },

            roleOptions(){
                //Build it
                var options = [
                    {value: null, text: '-- Select a Role --'}
                ];

                //Loop
                for(var i = 0; i < this.roles.length; i++)
                {
                    //Push
                    options.push({value: this.roles[i].id, text: this.roles[i].name});
                }

                //Send it
                return options;
            },

            titleOptions(){
                //Build it
                var options = [
                    {value: null, text: '-- Select a Title --'}
                ];

                //Loop
                for(var i = 0; i < this.titles.length; i++)
                {
                    //Push
                    options.push({value: this.titles[i], text: this.titles[i]});
                }

                //Send it
                return options;
            },
        },

        methods: {
            //Load the Account
            getAlert(){
                //Send it
                this.$http.get('/training-courses/alerts/edit' + (this.id ? '/' + this.id : ''))
                    .then(res => {
                        //Set data
                        this.alert = res.data.alert;
                        this.recipients = this.alert.recipients;
                        this.recipientTypes = res.data.recipientTypes;
                        this.roles = res.data.roles;
                        this.titles = res.data.titles;
                        this.courses = res.data.courses;
                    })
                    .catch(err => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            deleteRecipient(key){
                //Splice
                this.recipients.splice(key, 1);
            },

            courseSelected(course){
                //Check for Null
                if(course === null)
                {
                    return;
                }

                //Check if not setup yet
                if(!this.alert.filter_on_courses)
                {
                    this.alert.filter_on_courses = [];
                }

                //SEtup
                var exists = false;

                //Make sure the course is not already in the list of filter courses
                for (var i = this.alert.filter_on_courses.length - 1; i >= 0; i--)
                {
                    //Check it
                    if(this.alert.filter_on_courses[i].id == course.id)
                    {
                        exists = true;
                    }
                }

                //Append
                if(!exists)
                {
                    //Set
                    this.alert.filter_on_courses.push(course);
                }

                //Clear it
                this.$refs.courseSelect.clearSelection();
            },

            removeCourse(i){
                //Remove it
                this.alert.filter_on_courses.splice(i, 1);
            },

            saveAlert(){
                //Saving Flag
                this.saving = true;
                this.errors = {};

                //Set the values
                this.alert.recipients = this.recipients;

                //Send it
                this.$http.post('/training-courses/alerts/edit', this.alert)
                    .then(res => {
                        //Alert
                        this.$toastr.success('Training Course Alert Saved');

                        //Send to the View
                        this.$router.push('/training-courses/alerts/view/' + res.data.id);
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.error(error.response.data, 'Failed to Save Alert');
                        }
                    })
                    .then(() => {
                        //Reset
                        this.saving = false;
                    });
            }
        },

        mounted(){
            //Get it
            this.getAlert();
        }
    }
</script>
